import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { AUTH_STORAGE_KEYS, basicApi, coreApi } from '../utils/api';
import { getMyInfo, UserInfoResponse as User } from '../api/user';

export type AuthMode = 'basic' | 'core';

interface SessionOptions {
  mode?: AuthMode;
  basicUser?: User;
  coreUser?: User;
  basicAccessToken?: string;
  coreAccessToken?: string;
}

interface AuthContextType {
  mode: AuthMode;
  user: User | null;
  accessToken: string | null;
  basicUser: User | null;
  coreUser: User | null;
  basicAccessToken: string | null;
  coreAccessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userData: User, accessToken: string, options?: SessionOptions) => void;
  switchMode: (mode: AuthMode) => void;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getSavedUser = (key: string): User | null => {
  const savedUser = localStorage.getItem(key);
  if (!savedUser) return null;

  try {
    return JSON.parse(savedUser);
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

const saveValue = (key: string, value: unknown) => {
  if (value) localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  else localStorage.removeItem(key);
};

const clearSessionStorage = () => {
  Object.values(AUTH_STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  localStorage.removeItem('callis.auth.loginMode');
};

const getStoredMode = (): AuthMode => (
  localStorage.getItem(AUTH_STORAGE_KEYS.mode) === 'basic' ? 'basic' : 'core'
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<AuthMode>(getStoredMode);
  const [user, setUser] = useState<User | null>(() => getSavedUser(AUTH_STORAGE_KEYS.activeUser));
  const [accessToken, setAccessToken] = useState<string | null>(() => localStorage.getItem(AUTH_STORAGE_KEYS.activeToken));
  const [basicUser, setBasicUser] = useState<User | null>(() => getSavedUser(AUTH_STORAGE_KEYS.basicUser));
  const [coreUser, setCoreUser] = useState<User | null>(() => getSavedUser(AUTH_STORAGE_KEYS.coreUser));
  const [basicAccessToken, setBasicAccessToken] = useState<string | null>(() => localStorage.getItem(AUTH_STORAGE_KEYS.basicToken));
  const [coreAccessToken, setCoreAccessToken] = useState<string | null>(() => localStorage.getItem(AUTH_STORAGE_KEYS.coreToken));
  const [isLoading, setIsLoading] = useState(true);

  const checkSession = useCallback(async () => {
    const sessionMode = getStoredMode();
    setMode(sessionMode);
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.activeToken);
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      let userInfo: User;

      if (sessionMode === 'basic' && localStorage.getItem(AUTH_STORAGE_KEYS.basicToken)) {
        const response = await basicApi.get<User>('/user/info');
        userInfo = response.data;
        setBasicUser(userInfo);
        saveValue(AUTH_STORAGE_KEYS.basicUser, userInfo);
      } else if (sessionMode === 'core' && localStorage.getItem(AUTH_STORAGE_KEYS.coreToken)) {
        const response = await coreApi.get<User>('/user/info');
        userInfo = response.data;
        setCoreUser(userInfo);
        saveValue(AUTH_STORAGE_KEYS.coreUser, userInfo);
      } else {
        if (!localStorage.getItem(AUTH_STORAGE_KEYS.coreToken)) {
          localStorage.setItem(AUTH_STORAGE_KEYS.coreToken, token);
          setCoreAccessToken(token);
        }
        userInfo = await getMyInfo();
      }

      setUser(userInfo);
      saveValue(AUTH_STORAGE_KEYS.activeUser, userInfo);
    } catch (error) {
      clearSessionStorage();
      setMode('core');
      setUser(null);
      setAccessToken(null);
      setBasicUser(null);
      setCoreUser(null);
      setBasicAccessToken(null);
      setCoreAccessToken(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = (userData: User, token: string, options: SessionOptions = {}) => {
    const sessionMode = options.mode || 'core';

    setMode(sessionMode);
    setUser(userData);
    setAccessToken(token);
    saveValue(AUTH_STORAGE_KEYS.activeToken, token);
    saveValue(AUTH_STORAGE_KEYS.activeUser, userData);

    if (sessionMode === 'basic') {
      setBasicUser(options.basicUser || userData);
      setBasicAccessToken(options.basicAccessToken || token);
      setCoreUser(options.coreUser || null);
      setCoreAccessToken(options.coreAccessToken || null);
      saveValue(AUTH_STORAGE_KEYS.basicUser, options.basicUser || userData);
      saveValue(AUTH_STORAGE_KEYS.basicToken, options.basicAccessToken || token);
      saveValue(AUTH_STORAGE_KEYS.coreUser, options.coreUser);
      saveValue(AUTH_STORAGE_KEYS.coreToken, options.coreAccessToken);
    } else if (sessionMode === 'core') {
      setBasicUser(null);
      setBasicAccessToken(null);
      setCoreUser(options.coreUser || userData);
      setCoreAccessToken(options.coreAccessToken || token);
      localStorage.removeItem(AUTH_STORAGE_KEYS.basicUser);
      localStorage.removeItem(AUTH_STORAGE_KEYS.basicToken);
      saveValue(AUTH_STORAGE_KEYS.coreUser, options.coreUser || userData);
      saveValue(AUTH_STORAGE_KEYS.coreToken, options.coreAccessToken || token);
    }

    localStorage.setItem(AUTH_STORAGE_KEYS.mode, sessionMode);
  };

  const switchMode = useCallback((nextMode: AuthMode) => {
    const nextUser = nextMode === 'basic' ? basicUser : coreUser;
    const nextToken = nextMode === 'basic' ? basicAccessToken : coreAccessToken;
    if (!nextUser || !nextToken) return;

    setMode(nextMode);
    setUser(nextUser);
    setAccessToken(nextToken);
    saveValue(AUTH_STORAGE_KEYS.activeUser, nextUser);
    saveValue(AUTH_STORAGE_KEYS.activeToken, nextToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.mode, nextMode);
  }, [basicUser, coreUser, basicAccessToken, coreAccessToken]);

  const logout = async () => {
    const logoutRequests: Promise<unknown>[] = [];

    if (basicAccessToken) {
      logoutRequests.push(basicApi.post('/user/logout').catch((error) => console.error('Basic logout failed:', error)));
    }
    if (coreAccessToken) {
      logoutRequests.push(coreApi.post('/user/logout').catch((error) => console.error('Core logout failed:', error)));
    }
    if (!basicAccessToken && !coreAccessToken && accessToken) {
      logoutRequests.push(api.post('/user/logout').catch((error) => console.error('Logout failed:', error)));
    }

    await Promise.all(logoutRequests);
    clearSessionStorage();
    setMode('core');
    setUser(null);
    setAccessToken(null);
    setBasicUser(null);
    setCoreUser(null);
    setBasicAccessToken(null);
    setCoreAccessToken(null);
    window.location.href = '/login';
  };

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  return (
    <AuthContext.Provider value={{
      mode,
      user,
      accessToken,
      basicUser,
      coreUser,
      basicAccessToken,
      coreAccessToken,
      isAuthenticated: !!user,
      isLoading,
      login,
      switchMode,
      logout,
      checkSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
